/** Trees at or past their target harvest DBH, per species against the ISLH 2017 targets. */
import { Card, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { FALLBACK_MIN_DBH } from './maturity';
import { Section } from './Section';
import { useSpeciesName } from './species';
import type { TreeStats } from './stats';

export function MaturitySection({ stats }: { stats: TreeStats }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const name = useSpeciesName();
  const m = stats.maturity;
  return (
    <Section title={t('trees.sections.maturity')} className="report-maturity">
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <StatCard label={t('trees.maturity.count')} value={number(m.matureCount)} sub={`${number(m.maturePct, 1)} %`} accent testId="stat-mature" />
          <StatCard label={t('trees.maturity.volume')} value={number(m.matureVolume, 1)} unit="m³" testId="stat-mature-volume" />
        </SimpleGrid>
        <Card>
          <Table.ScrollContainer minWidth={560}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('trees.maturity.table.species')}</Table.Th>
                  <Table.Th ta="right">{t('trees.maturity.table.total')}</Table.Th>
                  <Table.Th ta="right">{t('trees.maturity.table.mature')}</Table.Th>
                  <Table.Th ta="right">{t('trees.maturity.table.pct')}</Table.Th>
                  <Table.Th ta="right">{t('trees.maturity.table.meanDbh')}</Table.Th>
                  <Table.Th ta="right">{t('trees.maturity.table.target')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {m.rows.map((r) => (
                  <Table.Tr key={r.genus}>
                    <Table.Td>{name(r.genus)}</Table.Td>
                    <Table.Td ta="right">{number(r.total)}</Table.Td>
                    <Table.Td ta="right">{number(r.mature)}</Table.Td>
                    <Table.Td ta="right">{number(r.pct, 1)}</Table.Td>
                    <Table.Td ta="right">{r.meanDbh === null ? '—' : number(r.meanDbh, 1)}</Table.Td>
                    <Table.Td ta="right">{r.target ? `${number(r.target.min)}–${number(r.target.max)}${r.overridden ? ' *' : ''}` : t('trees.maturity.fallback', { min: number(FALLBACK_MIN_DBH) })}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Text fz={12} c={brand.textMuted} mt="xs">
            {t('trees.maturity.caption')}
          </Text>
        </Card>
      </Stack>
    </Section>
  );
}
